/**
 * The Case filter model both Case lists share — `/air/case-management` and the
 * multi-tenant Cases tab.
 *
 * The URL is the state (P-16). Every group is an array of the case-list
 * endpoint's own values, so the params go to the API as they are. The URL is
 * as strict as the API, which answers an unknown or differently-cased value
 * with 400: there is no conversion of old links. The schema drops an invalid
 * value, so no request sends one; {@link inspectCaseListSearch} names what it
 * dropped, so the user can be told (`strict-case-list-search.ts`).
 *
 * Source is the one group with no allow-list. Its values are connector
 * definition ids; only the Source options can tell a real id from a stale one,
 * so that check is {@link withoutUnknownSources}, run once they load.
 */
import { z } from 'zod'

import { CASE_FILTER_GROUPS, nonEmpty } from './case-filter-groups'

import type {
	CaseFilterGroup,
	CaseFilterKey,
	CaseFilters,
} from './case-filter-groups'
import type { FilterValueOption } from '@/shared/data-table/types/filters'

/** A group read from the URL: the values kept, and whether any was dropped. */
type ReadGroup = { values: Array<string>; isInvalid: boolean }

/**
 * An array of strings, each passing `isValid`, deduplicated. Anything else — a
 * single string (the old shape), a non-string item, a value `isValid` rejects —
 * is dropped and marks the group invalid. Duplicates and an empty array are
 * dropped without marking it: no value the user chose is lost.
 */
const readGroup = (
	input: unknown,
	isValid: (value: string) => boolean,
): ReadGroup => {
	if (input === undefined) return { values: [], isInvalid: false }
	if (!Array.isArray(input)) return { values: [], isInvalid: true }
	const values = input.filter(
		(item): item is string => typeof item === 'string' && isValid(item),
	)
	return {
		values: [...new Set(values)],
		isInvalid: values.length < input.length,
	}
}

/**
 * Whether `group` accepts `value`: one of its option values, or — for Source,
 * which has no options — any non-blank id, since only the Source options can
 * check it.
 */
const isGroupValue =
	(group: CaseFilterGroup) =>
	(value: string): boolean => {
		// Widened on purpose: a union of option tuples has no callable `some`.
		const options: ReadonlyArray<FilterValueOption> | null = group.options
		return options === null
			? value.trim() !== ''
			: options.some((option) => option.value === value)
	}

/** A date the API can parse; anything else is invalid, `all` included. */
const readDate = (input: unknown) => {
	if (input === undefined) return { value: undefined, isInvalid: false }
	const valid = typeof input === 'string' && !Number.isNaN(Date.parse(input))
	return { value: valid ? input : undefined, isInvalid: !valid }
}

/**
 * One field per group, each a multi-select over the values the group accepts.
 * zod cannot type a shape built from a list, so the schema is cast to
 * {@link CaseFilters}: `readGroup` keeps each group to its own values, which
 * is what that type states.
 */
const caseFiltersSchema = z.object(
	Object.fromEntries(
		CASE_FILTER_GROUPS.map((group) => [
			group.key,
			z.preprocess(
				(input) => nonEmpty(readGroup(input, isGroupValue(group)).values),
				z.array(z.string()).optional(),
			),
		]),
	),
) as unknown as z.ZodType<CaseFilters>

const dateParam = z.preprocess(
	(input) => readDate(input).value,
	z.string().optional(),
)

/**
 * The search params both Case list routes validate. A `filters` object with no
 * group left is dropped, so a stale link does not leave `filters={}` behind.
 */
export const caseListSearchSchema = z.object({
	openedAfter: dateParam,
	openedBefore: dateParam,
	filters: z.preprocess((input) => {
		if (typeof input !== 'object' || input === null) return undefined
		const parsed = caseFiltersSchema.safeParse(input)
		if (!parsed.success) return undefined
		const groups = Object.entries(parsed.data).filter(
			([, value]) => value !== undefined,
		)
		return groups.length > 0 ? Object.fromEntries(groups) : undefined
	}, caseFiltersSchema.optional()),
	q: z.string().optional(),
})

export type CaseListSearch = z.output<typeof caseListSearchSchema>

/** The two ends of the opened-at range — top-level params, not `filters` groups. */
export type CaseDateField = 'openedAfter' | 'openedBefore'

/**
 * A part of the Case list URL that can hold an invalid value: a group, a date,
 * or `filters` itself when it is not an object.
 */
export type CaseListField = CaseFilterKey | CaseDateField | 'filters'

/** Menu order, so a message lists the groups the way the user sees them. */
const FIELD_ORDER: ReadonlyArray<CaseListField> = [
	'filters',
	...CASE_FILTER_GROUPS.map(({ key }) => key),
	'openedAfter',
	'openedBefore',
]

/** `fields` without duplicates, in menu order. */
export const orderCaseListFields = (
	fields: Iterable<CaseListField>,
): Array<CaseListField> => {
	const set = new Set(fields)
	return FIELD_ORDER.filter((field) => set.has(field))
}

/**
 * What the schema drops from a raw (unvalidated) Case list URL.
 *
 * - `search`: the parsed URL — what the list shows.
 * - `invalid`: the fields that held a value the API would reject, in menu
 *   order. The user chose these, so they must be told.
 *
 * Other params (`q`, the multi-tenant `tenants`) are not read.
 */
export const inspectCaseListSearch = (
	raw: Record<string, unknown>,
): {
	search: CaseListSearch
	invalid: Array<CaseListField>
} => {
	const invalid = new Set<CaseListField>()

	for (const field of ['openedAfter', 'openedBefore'] as const) {
		if (readDate(raw[field]).isInvalid) invalid.add(field)
	}

	const rawFilters = raw.filters
	if (rawFilters !== undefined) {
		if (
			typeof rawFilters !== 'object' ||
			rawFilters === null ||
			Array.isArray(rawFilters)
		) {
			invalid.add('filters')
		} else {
			const groups = rawFilters as Record<string, unknown>
			for (const group of CASE_FILTER_GROUPS) {
				if (readGroup(groups[group.key], isGroupValue(group)).isInvalid) {
					invalid.add(group.key)
				}
			}
		}
	}

	const parsed = caseListSearchSchema.parse(raw)

	return {
		search: parsed,
		invalid: orderCaseListFields(invalid),
	}
}

/**
 * `filters` without the Source ids that are not in `knownIds` — a deleted
 * integration's id, or an id that was never one. `null` when nothing is
 * dropped, so the caller does not rewrite a valid URL.
 */
export const withoutUnknownSources = (
	filters: CaseFilters,
	knownIds: ReadonlySet<string>,
): { filters: CaseFilters | undefined } | null => {
	const source = filters.source ?? []
	const kept = source.filter((id) => knownIds.has(id))
	if (kept.length === source.length) return null
	return { filters: withFilterGroup(filters, 'source', kept) }
}

/** The group with `value` flipped; `undefined` once nothing is selected. */
export const toggleFilterValue = <T extends string>(
	values: ReadonlyArray<T> | undefined,
	value: T,
): Array<T> | undefined => {
	const current = values ?? []
	return nonEmpty(
		current.includes(value)
			? current.filter((item) => item !== value)
			: [...current, value],
	)
}

/**
 * `filters` with one group replaced. An emptied group is removed, and so is
 * `filters` itself once no group is left — the URL shape the schema produces.
 */
export const withFilterGroup = (
	filters: CaseFilters,
	key: CaseFilterKey,
	values: ReadonlyArray<string> | undefined,
): CaseFilters | undefined => {
	const groups = Object.entries({ ...filters, [key]: values }).filter(
		([, group]) => group !== undefined && group.length > 0,
	)
	return groups.length > 0
		? (Object.fromEntries(groups) as CaseFilters)
		: undefined
}
